// modules
import { useLocation, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";

// constants
import { ReimbursementClaimStatus, ReimbursementVerificationReview, REIM_VERIFICATION_CONFIG_CODES, Routes } from "constants/global";

// components
import CountDownModal from "components/molecules/ModalContainer/CountDownModalContainer";

// redux
import { useAppDispatch, useAppSelector } from "app/hooks/hooks";
import { getReimbApprovalClaimDetails, approveReimbursementClaim, approvalClaimDetailsSelector, approvalClaimLoadingSelector } from "redux/reimbursement/reimbursementSlice";
import { getConfigData, getConfigSelector } from "redux/userList/userSlice";

// utils
import { allowApproveOrReject, getAttachmentsList } from "utils/reimbursementVerification";
import { validateRejectRetroClaimReason } from "utils/Validations/fieldValidations";
import { downloadFile } from "utils/downloadFile";

export type LocationState = {
	state: { reimb_summ_id: string };
	pathname: string;
};

export function useReimbursementApprovalReview() {
	const navigate = useNavigate();
	const location = useLocation();
	const { state } = location as LocationState;
	const dispatch = useAppDispatch();

	const claimDetails = useAppSelector(approvalClaimDetailsSelector);
	const claimsLoading = useAppSelector(approvalClaimLoadingSelector) === 1;
	const configDetails = useAppSelector(getConfigSelector);
	const { data } = claimDetails || {};
	const { data: configDetailsData } = configDetails || {};

	const [rejectionReasonErr, setRejectionReasonErr] = useState<boolean>(false);

	const initialValues = useMemo(() => {
		if (data && data?.reimb_summ && data?.reimb_summ?.length > 0) {
			return data?.reimb_summ[0];
		}
		return {};
	}, [data, claimsLoading]);

	const [rejectionReason, setRejectionReason] = useState<string>(initialValues?.verifier_remarks);

	useEffect(() => {
		if (state?.reimb_summ_id) {
			dispatch(getReimbApprovalClaimDetails({ reimb_summ_id: state?.reimb_summ_id }));
			dispatch(getConfigData({ config_mst_code: REIM_VERIFICATION_CONFIG_CODES.DATE_MASTER }));
		}
	}, []);

	const enableApproveRejectButtons = useMemo(() => {
		return allowApproveOrReject(configDetailsData);
	}, [configDetailsData, claimsLoading, data]);

	const goBack = useCallback(() => {
		navigate(Routes.DECLARATION_APPROVAL);
	}, [navigate]);

	const approveClaimSuccess = useCallback(() => {
		CountDownModal({
			timer: 3,
			title: ReimbursementVerificationReview?.APPROVE_CLAIM_MESSAGE,
			onTimeComplete: goBack
		});
	}, [CountDownModal, goBack]);

	const handleApproveClaim = useCallback(async () => {
		if (enableApproveRejectButtons) {
			const payload = {
				reimb_summ_id: Number(state.reimb_summ_id),
				reject_claim: "",
				amount: initialValues?.reimb_amount,
				voucher_group_code: initialValues?.voucher_group_code
			};

			try {
				const { message } = await dispatch(approveReimbursementClaim(payload)).unwrap();
				if (message === ReimbursementVerificationReview.APPROVE_CLAIM_MESSAGE) {
					approveClaimSuccess();
				}
			} catch (error) {
				// axios error
				const err = error as any;
				if (err?.response?.status === 401) {
					navigate(Routes.LOGIN);
				} else {
					CountDownModal({
						timer: 3,
						title: err?.response?.data?.errors?.[0]?.message,
						errorIcon: true
					});
				}
			}
		}
	}, [enableApproveRejectButtons, initialValues, CountDownModal, approveClaimSuccess]);

	const handleRejectClaim = useCallback(() => {
		if (enableApproveRejectButtons) {
			navigate(`${Routes.DECLARATION_APPROVAL}/${Routes.REIMBURSEMENT_REJECT_CLAIM}`);
		}
	}, [enableApproveRejectButtons, navigate]);

	const validateRejectionReason = useCallback(() => {
		let err = false;
		if (!rejectionReason) {
			err = true;
			setRejectionReasonErr(true);
		} else {
			err = !validateRejectRetroClaimReason(rejectionReason);
			setRejectionReasonErr(err);
		}
		return err;
	}, [rejectionReason, rejectionReasonErr, validateRejectRetroClaimReason]);

	const handleRejectionReasonChange = useCallback(
		(val: string) => {
			setRejectionReasonErr(false);
			setRejectionReason(val);
		},
		[rejectionReason, rejectionReasonErr]
	);

	const isStatusRejected = useMemo(() => {
		if (initialValues?.reimb_status?.toLowerCase() === ReimbursementClaimStatus.REJECTED?.toLowerCase()) {
			return true;
		}
		return false;
	}, [initialValues]);

	const isRejectionReasonUpdated = isStatusRejected && initialValues?.verifier_remarks !== rejectionReason;

	const rejectClaimSuccess = useCallback(() => {
		CountDownModal({
			timer: 3,
			title: ReimbursementVerificationReview?.CHANGES_MADE_SUCCESSFULLY,
			onTimeComplete: goBack
		});
	}, [CountDownModal, goBack]);

	const handleSaveChanges = useCallback(async () => {
		const hasErr = validateRejectionReason();

		if (isRejectionReasonUpdated && !hasErr) {
			const payload = {
				reimb_summ_id: Number(initialValues?.reimb_summ_id),
				reject_claim: "X",
				remarks: rejectionReason,
				voucher_group_code: initialValues?.voucher_group_code
			};

			try {
				const { message } = await dispatch(approveReimbursementClaim(payload)).unwrap();
				if (message === ReimbursementVerificationReview.REJECT_CLAIM_MESSAGE) {
					rejectClaimSuccess();
				}
			} catch (error) {
				// axios error
				const err = error as any;
				if (err?.response?.status === 401) {
					navigate(Routes.LOGIN);
				} else {
					CountDownModal({
						timer: 3,
						title: err?.response?.data?.errors?.[0]?.message,
						errorIcon: true
					});
				}
			}
		}
	}, [isRejectionReasonUpdated, initialValues, CountDownModal, rejectionReason]);

	const onFailure = useCallback(() => {
		CountDownModal({
			timer: 3,
			title: ReimbursementVerificationReview.NO_DATA_FOUND,
			errorIcon: true
		});
	}, [CountDownModal]);

	const handleDownload = useCallback(
		async (file_url: string) => {
			if (file_url) {
				try {
					await downloadFile(file_url);
				} catch (error) {
					onFailure();
				}
			} else {
				onFailure();
			}
		},
		[downloadFile, onFailure]
	);

	const isStatusApproved = useMemo(() => {
		if (initialValues?.reimb_status?.toLowerCase() === ReimbursementClaimStatus.APPROVED?.toLowerCase()) {
			return true;
		}
		return false;
	}, [initialValues]);

	return {
		claimDetails: initialValues,
		documents: getAttachmentsList(initialValues?.tenant_documents || ""),
		enableApproveRejectButtons,
		isStatusApproved,
		isStatusRejected,
		rejectionReason,
		rejectionReasonErr,
		isRejectionReasonUpdated,
		claimsLoading,
		loadingWithoutData: !data && claimsLoading,
		handleSaveChanges,
		handleApproveClaim,
		handleRejectClaim,
		goBack,
		validateRejectionReason,
		setRejectionReason: handleRejectionReasonChange,
		handleDownload
	};
}
